import { computed, toValue } from 'vue'
import { useMutation, useQuery } from '@tanstack/vue-query'

import { queryClient } from '@/api/query-client'
import * as accountService from './service'
import { accountQueryKey } from './query-keys'

export function useFetchUserInfoQuery({ options = {} } = {}) {
  return useQuery({
    queryKey: computed(() => accountQueryKey.fetchUserInfo()),
    queryFn: accountService.fetchUserInfo,
    ...options,
  })
}

export function useFetchPaymentInfoQuery({ options = {} } = {}) {
  return useQuery({
    queryKey: computed(() => accountQueryKey.fetchPaymentInfo()),
    queryFn: accountService.fetchPaymentInfo,
    staleTime: 0,
    ...options,
  })
}

export function useFetchInvoiceDetailsQuery({ params, options = {} }) {
  return useQuery({
    queryKey: computed(() => accountQueryKey.fetchInvoiceDetails(toValue(params))),
    queryFn: () => accountService.fetchInvoiceDetails({ params: toValue(params) }),
    enabled: computed(() => Object.values(toValue(params)).every(Boolean)),
    ...options,
  })
}

export function useCreateSetupIntentMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.createSetupIntent({ params }),
    ...options,
  })
}

export function useUpdateDefaultPaymentMethodMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.updateDefaultPaymentMethod({ params }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'payment-info'] })
    },
    ...options,
  })
}

export function useDeletePaymentMethodMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.deletePaymentMethod({ params }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'payment-info'] })
    },
    ...options,
  })
}

export function useFetchCouponDetailsQuery({ params, options = {} }) {
  return useQuery({
    queryKey: computed(() => accountQueryKey.fetchCouponDetails(toValue(params))),
    queryFn: () => accountService.fetchCouponDetails({ params: toValue(params) }),
    enabled: computed(() => Object.values(toValue(params)).every(Boolean)),
    staleTime: 0,
    ...options,
  })
}

export function useSubscribeMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.subscribe({ params }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'user-products'] })
    },
    ...options,
  })
}

export function useFetchProductDetailsQuery({ params, options = {} }) {
  return useQuery({
    queryKey: computed(() => accountQueryKey.fetchProductDetails(toValue(params))),
    queryFn: () => accountService.fetchProductDetails({ params: toValue(params) }),
    enabled: computed(() => Object.values(toValue(params)).every(Boolean)),
    staleTime: 0,
    ...options,
  })
}

export function useFetchUserProductsQuery({ options = {} } = {}) {
  return useQuery({
    queryKey: computed(() => accountQueryKey.fetchUserProducts()),
    queryFn: accountService.fetchUserProducts,
    staleTime: 0,
    ...options,
  })
}

export function useDeleteSubscription({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.deleteSubscription({ params }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'user-products'] })
    },
    ...options,
  })
}

export function useProrationMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.proration({ params }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'user-products'] })
    },
    ...options,
  })
}

export function usePaymentAttemptMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.paymentAttempt(params),
    ...options,
  })
}

export function useFetchTeamMembersQuery({ options = {} } = {}) {
  return useQuery({
    queryKey: computed(() => accountQueryKey.fetchTeamMembers()),
    queryFn: accountService.fetchTeamMembers,
    ...options,
  })
}

export function useDeleteAccountMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.deleteAccount({ params: toValue(params) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'user-info'] })
    },
    ...options,
  })
}

export function useDeleteUserAccountMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: params => accountService.deleteUserAccount({ params }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'user-info'] })
    },
    ...options,
  })
}

export function useReactivateAccountMutation({ options = {} } = {}) {
  return useMutation({
    mutationFn: accountService.reactivateAccount,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account', 'user-info'] })
    },
    ...options,
  })
}
